import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Event, useEventStore } from '../../store/useEventStore';
import { Button, Label } from 'flowbite-react';
import { HiArrowLeft } from 'react-icons/hi';
import dayjs from 'dayjs';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getEventById = useEventStore((state) => state.getEventById);
  const loading = useEventStore((state) => state.loading);

  const [event, setEvent] = useState<Event | null>(null);

  useEffect(() => {
    if (id) {
      loadEvent();
    }
  }, [id]);

  const loadEvent = async () => {
    if (!id) return;
    const eventData = await getEventById(id);
    if (eventData) {
      setEvent(eventData);
    }
  };

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            {loading ? 'Loading...' : 'Event not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Event Details" description="View event information" />
      <PageBreadcrumb pageTitle="Event Details" />
      <div className="space-y-6">
        <ComponentCard title="Event Information">
          <div className="space-y-6">
            {/* Header with Back Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {event.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      event.type === 'special'
                        ? 'bg-[#B8860B] text-white'
                        : event.type === 'service'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}
                  >
                    {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </span>
                </p>
              </div>
              <Button
                onClick={() => navigate(`/events`)}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiArrowLeft className="mr-2" /> Back to Events
                </span>
              </Button>
            </div>

            {/* Event Image */}
            {event.image && (
              <div className="w-full">
                <img src={event.image} alt={event.title} className="w-full h-96 object-cover rounded-lg" />
              </div>
            )}

            {/* Event Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label value="Title" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {event.title}
                </p>
              </div>
              <div>
                <Label value="Type" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  <span
                    className={`px-2 py-1 rounded text-sm font-semibold ${
                      event.type === 'special'
                        ? 'bg-[#B8860B] text-white'
                        : event.type === 'service'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}
                  >
                    {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </span>
                </p>
              </div>
              <div>
                <Label value="Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {event.date}
                </p>
              </div>
              <div>
                <Label value="Time" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {event.time}
                </p>
              </div>
              <div className="col-span-2">
                <Label value="Location" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {event.location}
                </p>
              </div>
              {event.description && (
                <div className="col-span-2">
                  <Label value="Description" style={{ fontFamily: 'Lato, sans-serif' }} />
                  <p className="text-gray-900 dark:text-white mt-1 whitespace-pre-wrap" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {event.description}
                  </p>
                </div>
              )}
              <div>
                <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {dayjs(event.createdAt).format('DD MMM YYYY, hh:mm A')}
                </p>
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>
    </>
  );
}
